import { Accordion, WysiwygBlock, ProductHero, RevealAnim, Heading, Button } from '@okam/brand-ui'
import { isHex } from '@okam/core-lib/index'
import { useIsClient } from '@okam/shared-ui/index'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { select } from 'radash'
import { useEffect, useState } from 'react'
import getUrl from '@/lib/utils/getUrl'
import { MainLayout } from 'components/layout'
import ProductGridWithLottie from 'components/products-listing/ProductGridWithLottie'
import Seo from 'components/Seo'
import { trackAddToCart, trackImportantClick } from 'gtm-analytics/tracking'
import { useTrackViewItem } from 'gtm-analytics/tracking/eventHooks'
import { toDirectusUrl } from 'lib/directus'
import { useProductQuery } from 'lib/react-query'
import { useColor } from 'providers/Color'
import { useUrlTranslation } from 'providers/UrlTranslation'
import tw, { css, theme } from 'twin.macro' /* eslint-disable-line */
import { PRODUCT_GRID_LOTTIE_LOCATION_INDEX } from '../../../../const'
import type { TProductPage, TFlavorItem } from '../types'

const ProductPage = ({ uri, locale, seo, directusProps }: TProductPage) => {
  const { colorScheme, setColorScheme } = useColor()
  const { setUrlTranslation } = useUrlTranslation()
  const router = useRouter()
  const { asPath, query } = router
  const { slug: productSlug } = query
  const pathWithoutSlug = asPath.slice(0, asPath.length - (productSlug?.length ?? 0))

  const { data: product, isSuccess } = useProductQuery(uri, locale, (productColorScheme) => {
    setColorScheme({
      title: productColorScheme?.title || 'Test',
      accent: isHex(productColorScheme?.accent) ? productColorScheme?.accent ?? '#ffffff' : '#ffffff',
      background: isHex(productColorScheme?.background) ? productColorScheme?.background ?? '#ffffff' : '#ffffff',
      accentMuted: productColorScheme?.accentMuted,
      backgroundMuted: productColorScheme?.backgroundMuted,
      outline: productColorScheme?.outline,
    })
    setUrlTranslation(`${pathWithoutSlug}${product?.slugInOtherLanguage?.translations?.[0]?.slug ?? ''}`)
  })

  const [isCardOpen, setIsCardOpen] = useState<{ isOpen: boolean; cardOpened: string }>({
    isOpen: false,
    cardOpened: '',
  })
  const [currentParentRoute, setCurrentParentRoute] = useState('')

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setIsCardOpen({ isOpen: false, cardOpened: '' })
    }

    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    setCurrentParentRoute(asPath.split('/')[1])

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeComplete)
    }
  }, [asPath, router.events])

  const {
    translations,
    featuredImage,
    additionalImages,
    animation,
    backgroundPattern,
    claims,
    flavorIcon,
    facet_options: facetOptions,
    animals,
    recommendations,
  } = product ?? {}
  const {
    sizes,
    title,
    flavor,
    description,
    buyCta,
    renCta,
    slug,
    feedingGuideline,
    guaranteedAnalysis,
    ingredients,
    ressources,
    specifications,
  } = translations?.[0] ?? {}

  const recommendedProductsData = recommendations?.recommendedProducts

  const filteredRecommendedProducts = recommendedProductsData?.filter(
    (recommendedProduct) => recommendedProduct?.productsId?.id !== product?.id,
  )

  const recommendedProducts = filteredRecommendedProducts?.map((recommendedProduct) => recommendedProduct?.productsId)

  const colorSchemeFallback = {
    accent: colorScheme?.accent ?? theme`colors.primaryHover.1`,
    background: colorScheme?.background ?? 'white',
    accentMuted: colorScheme?.accentMuted ? colorScheme?.accentMuted : theme`colors.primary.1`,
    backgroundMuted: colorScheme?.backgroundMuted ? colorScheme?.backgroundMuted : theme`colors.primary.2`,
    outline: colorScheme?.outline ? colorScheme?.outline : theme`colors.primaryHover.1`,
  }

  const { t } = useTranslation('common')
  const { isClient } = useIsClient()

  const buyOnlineText = t('BUTTON.BUY_ONLINE')
  const buyOnlineTextFr = t('BUTTON.BUY_ONLINE', { lng: 'fr' })

  const whereToFindUsText = t('BUTTON.FIND_US')
  const whereToFindUsTextFr = t('BUTTON.FIND_US', { lng: 'fr' })
  const whereToFindUrl = getUrl(t('ROUTES.FINDUS'))

  const recommendedTitle = t('PRODUCT.RECOMMENDED_TITLE')

  const accordionItems = {
    INGREDIENTS: ingredients,
    GUARANTEED_ANALYSIS: guaranteedAnalysis,
    RESSOURCES: ressources,
    FEEDING_GUIDELINES: feedingGuideline,
    SPECIFICATIONS: specifications,
  }

  const { index: productIndex } = query

  useTrackViewItem(product, Number(productIndex))

  const productTypeId = facetOptions?.[0]?.product_facets_item_id?.parent?.id
  const relatedProductsByProductType = product?.product_type?.products
  const relatedProduct = relatedProductsByProductType?.[0]
  type TRelatedProductType = typeof relatedProduct

  const createFlavorItem = (productType: TRelatedProductType): TFlavorItem => {
    const productTypeTranslations = productType?.translations?.[0]
    const productTypeFrTranslations = productType?.frTranslations?.[0]
    return {
      id: productTypeTranslations?.id ?? '',
      slug:
        `/${productType?.animals?.translations?.[0]?.title?.toLowerCase()}s/${productTypeTranslations?.slug}` ?? '#',
      title: productTypeTranslations?.flavor ?? productTypeTranslations?.title ?? '',
      titleFr: productTypeFrTranslations?.title ?? '',
      imageUrl: toDirectusUrl(productType?.flavorIcon?.id, productType?.flavorIcon?.filenameDownload),
    }
  }
  const relatedProducts = select(
    relatedProductsByProductType ?? [],
    (productType) => createFlavorItem(productType),
    (productType) => {
      const productFacetName = productType?.facet_options?.[0]?.product_facets_item_id?.translations?.[0]?.name
      return productTypeId === 'treats'
        ? productFacetName === facetOptions?.[0]?.product_facets_item_id?.translations?.[0]?.name
        : true
    },
  )

  return (
    <>
      <Seo directusData={directusProps} seo={seo?.seo} pageTitle={seo?.pageTitle} />
      <RevealAnim mode="popLayout" route={router.asPath}>
        <MainLayout footerProps={directusProps} logoColor={colorScheme?.accent}>
          {isSuccess && (
            <div css={[{ backgroundColor: colorSchemeFallback?.background }]}>
              <div tw="pt-40">
                <Button
                  icon="ArrowLeft"
                  iconPosition="left"
                  as="a"
                  buttonStyle="hollow"
                  href={`/${locale}/${currentParentRoute}`}
                  tw="bg-transparent w-fit mx-auto"
                  css={[{ color: colorScheme?.accent }]}
                >
                  <Heading tw="text-center" variant="h5" as="p">
                    {t('ROUTES.GOBACK', { p: currentParentRoute })}
                  </Heading>
                </Button>
                <ProductHero
                  additionalImages={[
                    ...(additionalImages ?? []).map((image) => {
                      return {
                        id: image?.directusFilesId?.id,
                        src: toDirectusUrl(image?.directusFilesId?.id, image?.directusFilesId?.filenameDownload),
                        description: image?.directusFilesId?.description ?? '',
                      }
                    }),
                  ]}
                  backgroundPatternUrl={toDirectusUrl(backgroundPattern?.id, backgroundPattern?.filenameDownload)}
                  buyCta={buyCta ?? ''}
                  claims={
                    claims?.map((claim) => ({
                      content: claim?.claimsId?.translations?.[0]?.content ?? '',
                      id: claim?.id,
                      icon:
                        {
                          description: claim?.claimsId?.icon?.description ?? '',
                          src: toDirectusUrl(claim?.claimsId?.icon?.id, claim?.claimsId?.icon?.filenameDownload),
                        } ?? {},
                    })) ?? []
                  }
                  colorScheme={colorSchemeFallback}
                  description={description ?? ''}
                  featuredImage={{
                    id: featuredImage?.id,
                    src: toDirectusUrl(featuredImage?.id, featuredImage?.filenameDownload),
                    description: featuredImage?.description ?? '',
                  }}
                  lottieUrl={toDirectusUrl(animation?.lottieFile?.id, animation?.lottieFile?.filenameDownload)}
                  sizes={sizes ?? ''}
                  title={title ?? ''}
                  flavorItems={relatedProducts}
                  flavor={{
                    id: flavorIcon?.id,
                    imageUrl: toDirectusUrl(flavorIcon?.id, flavorIcon?.filenameDownload),
                    title: flavor ?? title ?? '',
                    slug: `/${animals?.translations?.[0]?.title?.toLowerCase()}s/${slug}` ?? '#',
                  }}
                  onAddToCartPressed={() => {
                    trackImportantClick({
                      click_type: 'cta',
                      interaction_location: 'page',
                      link_url: buyCta ?? '',
                      link_text: buyOnlineText ?? '',
                      link_text_fr: buyOnlineTextFr ?? '',
                    })
                    trackAddToCart({
                      item_id: product?.id ?? '',
                      item_name: product?.translations?.[0]?.title ?? '',
                      item_category: product?.animals?.id ?? '',
                      item_category2: 'adult',
                      item_category3: product?.product_type?.id ?? '',
                      quantity: 1,
                      index: Number(productIndex),
                    })
                  }}
                  onWhereToFindUsPressed={() =>
                    trackImportantClick({
                      click_type: 'cta',
                      interaction_location: 'page',
                      link_url: whereToFindUrl ?? '',
                      link_text: whereToFindUsText ?? '',
                      link_text_fr: whereToFindUsTextFr ?? '',
                    })
                  }
                  onSelectionChanged={(text, textFr, link) => {
                    trackImportantClick({
                      click_type: 'bouton',
                      interaction_location: 'page',
                      link_url: getUrl(link),
                      link_text: text,
                      link_text_fr: textFr,
                    })
                  }}
                  buttons={{
                    buyMondou: { label: t('BUTTON.BUY_MONDOU'), link: buyCta },
                    buyRen: { label: t('BUTTON.BUY_REN'), link: renCta },
                    findUs: { label: t('BUTTON.FIND_US'), link: `${t('ROUTES.FINDUS')}` },
                  }}
                  selectAriaLabel={t('PRODUCT.SELECT_PRODUCT')}
                />

                {(guaranteedAnalysis || ingredients || ressources || specifications || feedingGuideline) &&
                  isClient && (
                    <div tw="flex flex-wrap gap-4 items-center justify-center p-8 pt-0">
                      {Object.entries(accordionItems).map(([translationKey, content]) => {
                        if (!content) return null
                        return (
                          <div
                            key={`accordionText-${translationKey}`}
                            tw="basis-full lg:basis-3/4 flex flex-col items-center"
                            css={[
                              css`
                                ul {
                                  ${tw`list-inside`}
                                }
                              `,
                            ]}
                          >
                            <Accordion
                              title={t(`PRODUCT.${translationKey}`)}
                              id={`accordionText-${translationKey}`}
                              color={colorSchemeFallback?.accent}
                              buttonColor={colorSchemeFallback?.outline}
                              ariaLabel={t(`PRODUCT.${translationKey}`)}
                              alignment="center"
                              onClick={() =>
                                trackImportantClick({
                                  click_type: 'accordéon',
                                  interaction_location: 'page',
                                  link_url: router.asPath ?? '',
                                  link_text: t(`PRODUCT.${translationKey}`) ?? '',
                                  link_text_fr: t(`PRODUCT.${translationKey}`) ?? '',
                                })
                              }
                              variant="single-product"
                            >
                              <WysiwygBlock content={content} />
                            </Accordion>
                          </div>
                        )
                      })}
                    </div>
                  )}
                {recommendedProducts && (
                  <div tw="flex flex-col justify-center pt-24">
                    <Heading as="h2" variant="h3" tw="font-bold text-center pb-16">
                      {recommendedTitle}
                    </Heading>
                    <ProductGridWithLottie
                      isCardOpen={isCardOpen}
                      setIsCardOpen={setIsCardOpen}
                      products={recommendedProducts}
                      animalType={product?.animals?.id}
                      animalLocation={PRODUCT_GRID_LOTTIE_LOCATION_INDEX}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </MainLayout>
      </RevealAnim>
    </>
  )
}

export default ProductPage
